"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { cn } from "~/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      "border border-solid border-primary/10 rounded-md",
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between p-4 font-medium transition-all [&[data-state=open]>svg]:rotate-180",
        "group",
        "border-0 relative",
        className,
      )}
      {...props}
    >
      {children}
      <div className="absolute bottom-0 start-4 w-4 h-[0.0625rem] bg-secondary translate-y-1/2 opacity-0 [[data-state=open]_&]:opacity-100 transition-opacity duration-200 [[data-state=open]_&]:delay-75" />
      <div className="bg-secondary-foreground border border-solid border-primary/10 p-1 rounded-full relative [[data-state=open]_&]:rotate-180 size-6 flex-shrink-0">
        <span
          className={cn(
            "[[data-state=open]_&]:rotate-0",
            "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 origin-center",
            "w-1/3 h-[0.0625rem] bg-secondary transition-all duration-200",
            "rotate-90",
          )}
        />
        <span
          className={cn(
            "[[data-state=open]_&]:rotate-90 [[data-state=open]_&]:h-1/3 [[data-state=open]_&]:w-[0.0625rem]",
            "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 origin-center",
            "w-1/3 h-[0.0625rem] bg-secondary transition-all duration-200",
          )}
        />
      </div>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden border-t border-solid border-t-primary/10 text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down p-4"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
